import SEO from '../components/SEO/SEO'
import Layout from '../layout/Layout'
import ContactForm from '../components/ContactForm/ContactForm'

const PrivacyPolicy = () => {
  return (
    <>
      <SEO />
      <Layout>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
          <div className="mx-auto">
            <div className="relative">
              <div className="absolute inset-0">
                <img
                  className="object-cover w-full h-full"
                  src="typing-on-macbook.png"
                  alt="People working on laptop together"
                />
              </div>
              <div className="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-32">
                <h1 className="text-4xl tracking-tight font-extralight sm:text-5xl lg:text-6xl text-blue-dark">
                  Privacy Policy
                </h1>
              </div>
            </div>
          </div>
        </div>

        <section>
          <div className="max-w-screen-xl px-4 py-12 mx-auto sm:px-6 lg:px-8 text-grey">
            <h3 className="pb-8 text-xl">Last Updated: August 29, 2024</h3>
            <p className="pb-2">
              Evry Healthcare, Inc., a Globe Life Company and its affiliated
              entities are committed to protecting the privacy and security of
              your personal information. We recognize that the privacy of your
              information is important. This Services Privacy Policy ("Privacy
              Policy" or "Policy") describes our practices in connection with
              information we collect through the online and mobile websites,
              platforms, services, and applications that we own or operate and
              that contain a link to this Privacy Policy (collectively,
              "Services").
            </p>
            <p className="my-4">
              As used in this Policy, terms such as "we," "us," "our," and
              "Company" refer to current and future affiliated entities,
              subsidiaries, agents, contractors, or vendors of Evry Healthcare,
              Inc. Our Services are intended for a United States audience. Any
              information you provide, including any personal information, will
              be transferred to and processed by a computer server located
              within the United States.
            </p>
            <p className="my-4">
              By using the Services, you consent to our collection, use,
              disclosure, and storage of information as described in this
              Privacy Policy. We may change this Policy at any time. If we do
              so, such change will appear on
              <a
                href="https://www.evryhealth.com/privacy-policy"
                className="mx-1 text-green"
              >
                https://www.evryhealth.com/privacy-policy
              </a>
              . You may always visit this Policy to learn of any updates. Your
              continued access to or use of the Services constitutes your
              consent to these changes to this Policy.
            </p>
            <h3 className="py-8 text-xl">When This Privacy Policy Applies</h3>
            <p>
              This Privacy Policy covers our treatment of personally
              identifiable information (“Personal Information”) that we gather
              when you are accessing or using our Services, but not to the
              practices of other companies we don’t own or control, or people
              that we don’t manage. We gather various types of Personal
              Information from our users, as explained in more detail below, and
              we use this Personal Information in connection with our Services.
              In certain cases, we may also share some Personal Information with
              third parties, but only as described below.
            </p>
            <p className="my-4">
              Our Policy does not apply to the practices of other companies or
              other websites or software applications that may be linked from or
              made available through our Services.
            </p>
            <p className="my-4">
              The inclusion of a link on our Services or the ability to utilize
              a third-party website or software application through our Services
              does not imply that we endorse, or otherwise monitor the privacy
              or security practices of that third-party website or software
              application or the accuracy of its content, and your use of the
              third-party website or software application is governed by the
              third-party's privacy policy.
            </p>
            <p className="my-4">
              Some of our products and services are regulated by certain state
              and federal laws, including the Health Insurance Portability and
              Accountability Act ("HIPAA") and the Gramm-Leach-Bliley Act and
              may have specific privacy practices ("Product Privacy Notices")
              such as a HIPAA Notice of Privacy Practices or other specific
              privacy practices that we want you to be aware.
            </p>
            <p className="my-4">
              This Privacy Policy is not intended to and does not create any
              contractual or other legal rights in or on behalf of any party.
            </p>
            <h3 className="py-8 text-xl">
              What Information We Collect About You
            </h3>
            <p>
              We may collect two basic types of information through the
              Services: (1) information you provide directly to us, and (2)
              information that is automatically provided to us or collected
              through your use of our Services (collectively, “Information”).
            </p>
            <p className="my-4">
              Such Information may include information that identifies you such
              as your full name, telephone number, email address, postal
              address, certain account numbers, your User ID that you create or
              is assigned to you, health and medical information, including
              Protected Health Information, financial information and other
              information described in this Privacy Policy.
            </p>
            <p className="my-4">
              When you use the Services, you may provide certain Information
              directly to us. For example, you may input a telephone number or
              email address into a webform or enter information into a chat
              functionality. We also may make certain information available to
              you on the Services that we collect from other sources. For
              example, we may pre-populate certain demographic information in an
              online form or make health or medical information viewable on the
              Services that you have provided to us on paper forms or maintained
              in other information systems.
            </p>
            <p className="my-4">
              We also may obtain Information that is automatically collected
              through the Services. The automatically collected Information may
              include demographic, de-identified, aggregated, or certain
              information from your device such as technical information about
              your device, web-browser information, and server log files
              collected by us or provided by you. See Cookies and Tracking for
              more information on automatically collected information.
            </p>
            <p className="my-4">
              You may limit the Information you provide or make available to us
              if you want to; however, that may limit your ability to access or
              use certain functions of the Services or to request certain
              services or information.
            </p>
            <h3 className="py-8 text-xl">Mobile Devices and Applications</h3>
            <p className="pb-4">
              In addition to the Information identified above, we may collect
              the following Information:
            </p>
            <p className="underline">Information that Identifies You:</p>
            <ul className="my-4 ml-6 list-disc">
              <li>Health, medical, therapy, or financial information;</li>
              <li>Information created by the Company;</li>
              <li>
                Location data such as GPS, Wi-Fi, or carrier network location
                (see below for more details); and
              </li>
              <li>
                User files stored on your device like calendars, photos, and
                videos, if you grant permission through your device settings.
              </li>
            </ul>
            <p className="underline">What You Do on Your Device:</p>
            <ul className="my-4 ml-6 list-disc">
              <li>
                Camera use. Certain features may have access to your camera if
                you grant permission in your device settings;
              </li>
              <li>Local storage;</li>
              <li>Phone dialer;</li>
              <li>
                Use of screen, e.g., what points are touched, frequency, etc.;
                and
              </li>
              <li>Patterns of app usage.</li>
            </ul>
            <p className="underline">Device or System Data:</p>
            <ul className="my-4 ml-6 list-disc">
              <li>Mobile Device Identifier, e.g., UDID, Android ID; and</li>
              <li>
                Technical information about your device and system and
                application software, e.g., type of phone, Operating System
                (OS), and IP address.
              </li>
            </ul>
            <p className="my-4">
              We may obtain location data from your device to provide
              location-related services (e.g., driving directions or distance
              calculation, via the mobile application). You may withdraw consent
              to use precise, real-time, or network location data at any time by
              turning off the location-based feature on your mobile device or by
              not using any location-based features. If you withdraw your
              consent, functionality associated with precise, real-time, or
              network location (e.g., navigation) will no longer work.
            </p>
            <h3 className="py-8 text-xl">How We Use Your Information</h3>
            <p className="underline">We may use your Information:</p>
            <ul className="my-4 ml-6 list-disc">
              <li>
                To respond to an email, chat or other instant message or
                particular request from you
              </li>
              <li>To communicate with you</li>
              <li>
                To provide you with content, services, and functionality through
                our Services or other services that we may offer
              </li>
              <li>
                To process an application for a product or service as requested
                by you
              </li>
              <li>
                To authenticate you on any portion of our Services and with
                vendors acting on our behalf
              </li>
              <li>To administer surveys and promotions</li>
              <li>To personalize your experience on our Services</li>
              <li>
                To provide you with informational or promotional offers, as
                permitted by law, that we believe may be useful to you, such as
                information about products or services provided by us or other
                businesses
              </li>
              <li>
                To perform analytics and to improve our products, Services, and
                advertising
              </li>
              <li>
                To facilitate the provision of software updates and product
                support
              </li>
              <li>
                To improve products and other services related to the Services
                or to provide services or technologies to you
              </li>
              <li>
                To comply with applicable laws, regulations, and legal process
              </li>
              <li>To protect someone's health, safety, or welfare</li>
              <li>
                To protect our rights, the rights of affiliates or related third
                parties, or to take appropriate legal action, such as to enforce
                our Terms of Use
              </li>
              <li>
                To keep a record of our transactions and communications; and
              </li>
              <li>
                As otherwise necessary or useful for us to conduct our business,
                so long as such use is permitted by law or for any other purpose
                with your consent.
              </li>
            </ul>
            <p className="my-4">
              We may use Information to contact you through any contact
              information you provide through our Services, including any email
              address, telephone number, cell phone number, or fax number. We
              may communicate, electronically or via telephone with you about
              your benefit plan, programs, products, or services that are or may
              be available to you in connection with your transactions with us
              including, but not limited to, Services updates, account
              information, general wellness, prescription or appointment
              reminders, general health information, newsletters, and surveys.
              These electronic communications may contain protected health
              information. You acknowledge and accept that such communications
              may be sent unencrypted and there is some risk of disclosure or
              interception of the contents of these communications. To learn
              more about our communications practices.
            </p>
            <p className="my-4">
              We may, when permitted, combine your Information with other
              information, whether online or offline, maintained or available to
              us from you or from other sources, such as from our vendors, and
              we may use and disclose combined data for the purpose described in
              this Section or for internal business purposes. We may, when
              permitted, use and disclose de-identified and aggregated data for
              any purpose, which may include, without limitation, disclosures to
              third parties for analytics purposes such as evaluating the
              Services and providing additional benefits, programs, and
              services.
            </p>
            <h3 className="py-8 text-xl">How We Share Your Information</h3>
            <p>
              We do not rent or sell your Personal Information in personally
              identifiable form to anyone. We will only share your Information
              with third parties as outlined in this Policy and as otherwise
              permitted by law or as permitted with your consent.
            </p>
            <p className="my-4">
              Evry will not sell, license, transmit or disclose outside of Evry
              Health the Information you provide to us unless (a) expressly
              authorized by you, (b) necessary to enable our Business Associates
              to perform certain functions for us, or (c) required or permitted
              by law. In all cases, we will disclose the information consistent
              with applicable laws and regulations and we will require the
              recipient to protect the information and use it only for the
              purpose it was provided and as necessary to assist us. Evry Health
              takes the Health Insurance Portability and Accountability Act of
              1996 (HIPAA) seriously and provides appropriate safeguards to your
              protected health information (PHI) – this may include your name,
              address, social security number, email address, telephone number
              and certain claims data.
            </p>
            <p className="my-4">
              We may de-identify your Personal Information in accordance with
              applicable state and federal law, and provide that information to
              our partners. We may also provide aggregate usage information to
              our partners (or allow partners to collect that information from
              you), who may use such information to understand how often and in
              what ways people use our Services, so that they, too, can provide
              you with an optimal online experience. We disclose usage data for
              our non-member portal site to partners who may provide you with
              additional information on Evry Health products and services.
            </p>
            <p className="my-4">
              We may choose to buy or sell assets, and may share and/or transfer
              customer information in connection with the evaluation of and
              entry into such transactions. Also, if we (or our assets) are
              acquired, or if we go out of business, enter bankruptcy, or go
              through some other change of control, Personal Information could
              be one of the assets transferred to or acquired by a third party.
            </p>
            <p className="my-4">
              We may share Information in response to a court order, subpoena,
              search warrant, or to comply with law or regulation. We may
              cooperate with law enforcement authorities in investigating and
              prosecuting activities that are illegal, violate our rules, or may
              be harmful to other visitors.
            </p>
            <p className="my-4">
              We may also share Information within the Company, including among
              affiliates, or with our parent company, or subsidiaries.
            </p>
            <p className="my-4">
              We may retain your information as needed for business purposes.
              Notwithstanding any provision to the contrary, we will retain,
              access, use and disclose your information as we believe is
              necessary to comply with our legal obligations, resolve disputes,
              enforce our Terms of Service and other agreements, or to protect
              the rights, property or safety of Evry Health, our employees, our
              users or others. For example, we may hire a third-party company to
              help us send and manage email, and we might provide that
              third-party company with your email address and certain other
              Information for them to send you an email message on our behalf.
              Similarly, we may hire third-party companies to host or operate
              some of our Services and related computers and software
              applications. For more information about our privacy practices,
              including what information is provided to our Business Associates
              for payment, treatment and healthcare operations purposes, please
              see our Notice of Privacy Practices.
            </p>

            <h3 className="py-8 text-xl"> Personal Health Information (PHI)</h3>
            <p>
              We may collect, use, and disclose health information for your
              treatment, to pay for your health care and to operate our
              business. For example, we may collect, use, and disclose your
              health information:
            </p>
            <ul className="my-4 ml-6 list-disc">
              <li>
                For payment of premiums due us, to determine your coverage, and
                to process claims for health care services you receive,
                including for subrogation (when permitted by applicable law) or
                coordination of other benefits you may have. For example, we may
                tell a doctor whether you are eligible for coverage and what
                percentage of the bill may be covered.
              </li>
              <li>
                For treatment, authorizations, and access to medical records. We
                may collect, use, and disclose health information to aid in your
                treatment or the coordination of your care. For example, we may
                collect information from, or disclose information to, your
                physicians or hospitals to help them provide medical care to
                you.
              </li>
              <li>
                For operations. We may collect, use, and disclose health
                information as necessary to operate and manage our business
                activities related to providing and managing your health care
                coverage. For example, we might talk to your physician to
                suggest a disease management or wellness program that could help
                improve your health or we may analyze data to determine how we
                can improve our services. We may also de identify health
                information in accordance with applicable laws. After that
                information is de-identified, the information is no longer
                subject to this notice and we may use the information for any
                lawful purpose.
              </li>
              <li>
                To Provide You Information on Health Related Programs or
                Products such as alternative medical treatments and programs or
                about health-related products and services, subject to limits
                imposed by law.
              </li>
              <li>
                For Plan Sponsors. If your coverage is through an employer
                sponsored group health plan, we may share summary health
                information and enrollment and disenrollment information with
                the plan sponsor.
              </li>
            </ul>

            <h3 className="py-8 text-xl"> How to Manage Your Information</h3>
            <p>
              Our Services may permit you to view your profile, if applicable,
              and access related Information about you and to request changes to
              such Information. Please remember, however, if we have already
              disclosed some of this Information to third parties, we may not
              have access to that disclosed information and may not be able to
              force the modification of any Information by the third parties to
              whom we have made those disclosures.
            </p>
            <h3 className="py-8 text-xl">
              Communication Preference Management
            </h3>
            <p>
              Our Services may permit you to select how you would like to
              receive certain communications. You can view and update your
              communication preferences in your Account/Profile settings after
              logging into the Services.
            </p>
            <p className="my-4">
              We may offer mobile applications that enable us to communicate
              with you through push notifications. Where mobile applications are
              offered, you may be able to manage push notifications in your
              mobile phone or tablet settings. You may also be able to control
              preview settings in your email applications.
            </p>
            <p className="my-4">
              If you need additional assistance in opting-out of a
              communication, please contact Member Services for assistance.
              Please be aware that opt-outs may not apply to certain types of
              communications, such as account status, Service updates, or other
              communications.
            </p>
            <h3 className="py-8 text-xl">Informational Calls and Texts</h3>
            <p>
              When you provide us a telephone number, whether landline or
              mobile, we may contact you, using automated, pre-recorded, or
              non-automated means, to provide you information about existing
              benefits, programs, products, services, or tools.
            </p>
            <p className="my-4">
              Personal Information collected through our texting programs will
              not be shared, sold, or disclosed to third parties for their own
              marketing purposes.
            </p>
            <p className="my-4">
              We reserve the right to modify this section at any time. The
              modified section will be effective immediately upon posting. Your
              continued receipt of Informational Electronic Communications and
              Informational Calls and Texts will constitute your acceptance of
              the modified section.
            </p>
            <h3 className="py-8 text-xl">Cookies and Tracking</h3>
            <p>
              The Company may use various technologies, including cookies,
              tokens, tags, web logs, web beacons, scripts, and web server logs
              to gather automatically collected information and may aggregate
              this information from visitors of our Services or to enable
              certain features of our Services. This information may include
              demographic data, technical information about the technology
              (e.g., phone, computer) you use to connect to the Services, web
              browser information, your IP address, and browsing behavior such
              as pages visited and how often they are visited (collectively,
              "Activity Information"). We may also use third-party analytics
              companies to provide these services.
            </p>
            <p className="my-4">
              We may also allow third-parties to use cookies and other
              technologies to collect Activity Information and to track browsing
              activity over time and across third-party websites such as web
              browsers used to read our Services, which websites are referring
              traffic or linking to our Services, and to deliver targeted
              advertisements to you. We do not control these third-party
              technologies, and their use of such technologies is governed by
              their own privacy policies.
            </p>
            <p className="my-4">
              Activity Information is captured using various technologies and
              may include cookies. "Cookies" are small text files that may be
              placed on your computer or mobile device when you visit an online
              Service or click on a URL using your web browser. Cookies may
              include "single-session cookies" which generally record
              information during only a single visit to a website and then are
              erased, and "persistent" cookies which are generally stored on a
              computer or mobile device unless or until they are deleted or are
              set to expire. You may disable cookies and similar items by
              adjusting your browser preferences at any time; however, this may
              limit your ability to take advantage of all the features on our
              Services.
            </p>
            <p className="mt-4">
              We gather Activity Information about you to improve the quality of
              our services, such as the best method and time to contact you.
              Without limiting the other ways in which we may use Information as
              described herein, we may otherwise use and disclose your Activity
              Information unless restricted by this Policy or by law. Some
              examples of the ways we use your Activity Information include:
            </p>
            <ul className="my-4 ml-6 list-disc">
              <li>
                {' '}
                Customizing your experiences, including managing and recording
                your preferences;
              </li>
              <li> Authenticating your account information;</li>
              <li> Marketing, product development, and research purposes;</li>
              <li> Tracking resources and data accessed on the Services;</li>
              <li>
                {' '}
                Developing reports regarding Online Service usage, activity, and
                statistics;
              </li>
              <li> Assisting users experiencing problems with our services;</li>
              <li> Updating and servicing our Services;</li>
              <li>
                {' '}
                Enabling certain functions and tools on the Services; and
              </li>
              <li>
                {' '}
                Tracking paths of visitors to the Services and within the
                Services.
              </li>
            </ul>
            <p className="mt-4">
              As described above, we may use tracking technologies that allow us
              to recognize your device when you return to our Services within a
              period of time, as determined by us, and to support automatic
              login to your Services. To maintain your privacy, you should
              affirmatively log out of your account prior to your session ending
              (whether you end your session or we end your session, for example
              if our Services has "timed out" - i.e., we have ended your session
              automatically after a period of inactivity as determined by us in
              our sole discretion). Unless you affirmatively log out of your
              account, you may be automatically logged back in the next time you
              or any user of your devices visits the Services.
            </p>
            <h3 className="py-8 text-xl">Retention of Data</h3>
            <p>
              We will retain your personally identifiable information (PII) and
              Activity Information for as long as is necessary for the purposes
              set out in this Privacy Policy. We will retain and use your PII to
              the extent necessary to comply with our legal obligations (for
              example, if we are required to retain your data to comply with
              applicable laws), resolve disputes, and enforce our legal
              agreements and policies.
            </p>
            <h3 className="py-8 text-xl">Protecting Your Information</h3>
            <p>
              We maintain administrative, technical, and physical safeguards
              designed to protect the Information that you provide on our
              Services. These safeguards vary based on the sensitivity of the
              Information that is being collected, used, and stored. We cannot
              guarantee the security of our Services, nor can we guarantee the
              security of the Information you transmit to us over the Internet,
              including your use of email. We are not liable for the illegal
              acts of third parties such as criminal hackers.
            </p>
            <p className="my-4">
              It is your responsibility to safeguard the devices you use to
              access our Services (such as laptops, tablets and mobile devices),
              and to use appropriate security settings on those devices. If
              those devices are lost, stolen or misplaced, others may be able to
              access your account and your personal Information using those
              devices.
            </p>
            <p className="my-4">
              You agree that we are not responsible for any harm that may result
              from someone accessing your account or personal Information on any
              computer or device where you do not, for any reason, take the
              necessary steps to log out of your account prior to ending a
              session on such device or computer.
            </p>
            <p className="my-4">
              We retain Information for as long as necessary for the purpose for
              which it is collected, subject to a longer period if the
              Information is relevant to a legal challenge.
            </p>
            <h3 className="py-8 text-xl">Children Under 16</h3>
            <p>
              We will not intentionally collect any personal information (as
              that term is defined in the Children's Online Privacy Protection
              Act) from children under the age of 13 through our Services
              without receiving parental consent. If you think that we have
              collected such personal information from a child under the age of
              13 through our Services, please contact us immediately.
            </p>
            <h3 className="py-8 text-xl">Your email</h3>
            <p>
              We welcome your comments or questions about our Site and Services.
              You can email your comments to our customer service center at
              support@evryhealth.com. We will share your comments and questions
              with our customer service representatives and those employees most
              capable of addressing your questions and concerns. Please note
              that your email, like all non-encrypted Internet email
              communications, may be accessed and viewed by other Internet
              users, without your knowledge and permission, while in transit to
              us. For that reason, to protect your privacy, please do not use
              email to communicate information to us that you consider
              confidential. If you wish, you may contact us instead by telephone
              at 1-855-579-3879 (EVRY).
            </p>
            <h3 className="py-8 text-xl">How can you stop receiving emails?</h3>
            <p>
              Each marketing email we send to you contains an unsubscribe link
              through which you may easily opt-out of receiving future
              commercial emails from us. If you do not wish to receive
              additional commercial emails from Evry Health, simply click the
              unsubscribe link and follow the instructions to unsubscribe your
              email address. If you have unsubscribed but continue to receive
              email from us or from one of our customers, you may report this to
              us by calling 1-855-579-3879 (EVRY). Please note that unsubscribe
              requests may take up to 7 - 10 days to process. You will have the
              ability to opt out of any marketing or advertising communications,
              but we may still send you communications relating to your Account
              for purposes important to the Services, such as password recovery
              or a payment reminder.
            </p>
            <h3 className="py-8 text-xl">
              Social Security Number Protection Policy
            </h3>
            <p>
              It is our policy to protect the confidentiality of Social Security
              numbers (“SSNs”) that we receive or collect in the course of
              business. We secure the confidentiality of SSNs through various
              means, including physical, technical, and administrative
              safeguards that are designed to protect against unauthorized
              access. It is our policy to limit access to SSNs to that which is
              lawful and to prohibit unlawful disclosure of SSNs.
            </p>
            <h3 className="py-8 text-xl">Contact Us</h3>
            <p>
              Contact us regarding this Policy or related privacy practices. If
              you believe we or any company associated with us has misused your
              Information, please contact us immediately.
            </p>
            <p className="mt-8">Evry Health</p>
            <p>Member Services </p>
            <a href="tel:18555793879" className="font-bold text-green">
              1-855-579-3879 (EVRY)
            </a>
            <p>PO Box 571208</p>
            <p>Dallas, TX 75357</p>
          </div>
        </section>
      </Layout>
    </>
  )
}

export default PrivacyPolicy
